import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { submitContactToWebhook } from '@/lib/webhook';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { FaLinkedinIn, FaTwitter, FaEnvelope, FaYoutube, FaInstagram, FaFacebookF } from 'react-icons/fa';
import { toast } from 'sonner';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const normalizeWebsiteUrl = (raw) => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    interest: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current) {
        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'error-callback': () => setTurnstileToken(null),
        });

        return () => {
          if (window.turnstile && widgetId) {
            window.turnstile.remove(widgetId);
          }
        };
      }
    };

    if (document.readyState === 'complete') {
      return renderWidget();
    }

    const onLoad = () => {
      renderWidget();
    };

    window.addEventListener('load', onLoad);
    return () => window.removeEventListener('load', onLoad);
  }, []);

  useEffect(() => {
    if (submitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!turnstileToken) {
      toast.error('Please complete the security check before submitting.');
      return;
    }
    const normalizedWebsite = normalizeWebsiteUrl(formData.website);

    setIsSubmitting(true);
    
    try {
      await submitContactToWebhook({
        name: formData.name,
        email: formData.email,
        website: normalizedWebsite,
        interest: formData.interest,
        message: formData.message,
        turnstileToken
      });

      // Also store locally as backup
      const contacts = JSON.parse(localStorage.getItem('contactRequests') || '[]');
      contacts.push({
        ...formData,
        website: normalizedWebsite,
        timestamp: new Date().toISOString(),
        id: Date.now()
      });
      localStorage.setItem('contactRequests', JSON.stringify(contacts));
      
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again or email us directly at hello@evolutiongeo.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'email') {
      if (!value) {
        setEmailError('Email is required.');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        setEmailError('Please enter a valid email address.');
      } else {
        setEmailError('');
      }
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6 overflow-hidden">
            <img
              src="/assets/evolutionlogo-light-mode.png"
              alt="Evolution GEO"
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold mb-4">
            Thank You for Reaching Out!
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            We've received your message and will get back to you within one business day.
          </p>
          <p className="text-sm text-muted-foreground">
            In the meantime, feel free to explore our{' '}
            <Link to="/resources" className="underline underline-offset-2">
              resources
            </Link>{' '}
            or check out our case studies.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            Let's Talk About Your{' '}
            <span className="text-gradient">AI Visibility</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground">
            Whether you want a free GEO/AIO audit, a custom automation project, or just have questions—we're here.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-display font-bold mb-6">Get in Touch</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Have questions about GEO/AIO, AI ads, or AI agents? We're happy to chat and help you understand how these strategies can work for your business.
              </p>
              
              <div className="space-y-6">
                <Card className="border-border">
                  <CardContent className="flex items-start space-x-4 pt-6">
                    <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold mb-1">Email Us</h3>
                    <a href="mailto:hello@evolutiongeo.com" className="text-primary hover:underline">
                      hello@evolutiongeo.com
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardContent className="pt-6">
                    <h3 className="font-display font-semibold mb-4">Follow Us</h3>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href="https://linkedin.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-md border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      >
                        <FaLinkedinIn size={16} />
                      </a>
                      <a
                        href="https://twitter.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-md border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      >
                        <FaTwitter size={16} />
                      </a>
                      <a
                        href="mailto:hello@evolutiongeo.com"
                        className="w-9 h-9 rounded-md border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      >
                        <FaEnvelope size={16} />
                      </a>
                      <a
                        href="https://youtube.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-md border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      >
                        <FaYoutube size={16} />
                      </a>
                      <a
                        href="https://instagram.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-md border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      >
                        <FaInstagram size={16} />
                      </a>
                      <a
                        href="https://facebook.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-md border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      >
                        <FaFacebookF size={16} />
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-primary/50 bg-primary/5">
                  <CardContent className="pt-6">
                    <h3 className="font-display font-semibold mb-2">Prefer a Free Audit?</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      If you're interested in understanding your current AI visibility, start with our free GEO/AIO audit instead.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                      asChild
                    >
                      <a href="/free-audit">Get Free Audit</a>
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardContent className="pt-6">
                    <h3 className="font-display font-semibold mb-2">Prefer to Schedule a Call?</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Book a quick introduction call to walk through your goals and see if Evolution GEO is a good fit.
                    </p>
                    <Button variant="outline" size="sm" asChild>
                      <a href="/contact">Schedule a Call</a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="border-border shadow-lg">
                <CardHeader>
                  <CardTitle className="font-display text-2xl">Send Us a Message</CardTitle>
                  <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                      />
                      {emailError && (
                        <p className="text-xs text-destructive mt-1">{emailError}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        name="website"
                        type="text"
                        value={formData.website}
                        onChange={handleChange}
                        placeholder="https://yourwebsite.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="interest">I'm interested in *</Label>
                      <Select
                        value={formData.interest}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, interest: value }))}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="geo-aio">GEO/AIO Optimization</SelectItem>
                          <SelectItem value="ai-ads">AI Ads</SelectItem>
                          <SelectItem value="ai-agents">AI Agents & Automations</SelectItem>
                          <SelectItem value="all">All Services</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your goals and challenges..."
                        rows={5}
                      />
                    </div>

                    {/* Cloudflare Turnstile */}
                    {TURNSTILE_SITE_KEY && (
                      <div className="space-y-2">
                        <div
                          ref={turnstileRef}
                          className="cf-turnstile-widget"
                          data-sitekey={TURNSTILE_SITE_KEY}
                        />
                        <p className="text-xs text-muted-foreground text-center">
                          Protected by <span className="font-medium">Cloudflare Turnstile</span>
                        </p>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground text-center">
                      By submitting this form, you agree to our{' '}
                      <Link to="/terms" className="underline underline-offset-2">
                        Terms &amp; Conditions
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="underline underline-offset-2">
                        Privacy Policy
                      </Link>
                      .
                    </p>

                    {isSubmitting && (
                      <p className="text-xs text-muted-foreground text-center">
                        Sending your message&hellip; please wait while we confirm it&apos;s received.
                      </p>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full gradient-primary text-primary-foreground shadow-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
